// SaveStatusIndicator — a thin ambient status pill for the header.
//
// NOT a toast (§C UX principles): no animation, no auto-dismiss; lives in
// the header so it's always visible without stealing focus. Renders one of:
//
//   'idle'   → blank
//   'saving' → "Saving…"
//   'saved'  → "Saved 2:14 PM"
//   'error'  → "Save failed — retry" (clickable; calls `onRetry`)

import type { SaveStatus } from '@/hooks/useLeaseDraft';

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  lastSavedAt: Date | null;
  onRetry?: () => void;
  errorCount?: number;
  onDiscard?: () => void;
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function SaveStatusIndicator({
  status,
  lastSavedAt,
  onRetry,
  errorCount = 0,
  onDiscard,
}: SaveStatusIndicatorProps): React.JSX.Element | null {
  if (status === 'idle' && !lastSavedAt) return null;

  if (status === 'saving') {
    return (
      <span className="pill" data-tone="info" aria-live="polite">
        <span className="dot" data-tone="info" data-pulse="true" />
        Saving…
      </span>
    );
  }
  if (status === 'saved' || (status === 'idle' && lastSavedAt)) {
    return (
      <span className="pill" data-tone="success" aria-live="polite">
        <span className="dot" data-tone="success" />
        Saved{lastSavedAt ? ` ${formatTime(lastSavedAt)}` : ''}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span
        className="pill"
        data-tone="danger"
        aria-live="assertive"
        style={{ gap: 'var(--space-3)' }}
      >
        <span className="dot" data-tone="danger" />
        Save failed
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="btn"
            data-variant="link"
            data-size="sm"
          >
            retry
          </button>
        )}
        {errorCount >= 3 && onDiscard && (
          <button
            type="button"
            onClick={onDiscard}
            className="btn"
            data-variant="link"
            data-size="sm"
          >
            discard local changes and reload
          </button>
        )}
      </span>
    );
  }
  return null;
}
